import { useState, useEffect } from 'react';

/**
 * useFocusDetection — Detects when the player loses focus using:
 * - Page Visibility API (document.visibilityState)
 * - Window blur/focus events
 *
 * Returns `true` when the page is hidden or the window has lost focus.
 * Used to toggle the BlurOverlay and audio-continues-on-blur behavior.
 *
 * Validates: Req 1.31, 1.33
 */
export function useFocusDetection(): boolean {
  const [isFocusLost, setIsFocusLost] = useState(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsFocusLost(document.visibilityState === 'hidden');
    };

    const handleWindowBlur = () => {
      setIsFocusLost(true);
    };

    const handleWindowFocus = () => {
      setIsFocusLost(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  return isFocusLost;
}
